import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import LinesEllipsis from 'react-lines-ellipsis';
import { Button } from 'reactstrap';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';

import { COLUMN_TYPES } from '../../../../config/app.config';
import {
  AppointmentInfo,
  AvailabilityTable,
  CreateAppointmentBlock,
  PreferredDatesTable,
} from '../Components';
import {
  useAppointmentRequestModalContext,
  parseAvailabilityInfo,
  useAppointmentRequestsTableHeaders,
} from '../utils';
import { useNestedModal } from '../../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';
import { TextDisplayModal, ImageGalleryModal } from '../../../modals/ContentModals';
import { DataTable } from '../../../modals/ModalTables';
import ModalPdfViewer from '../../Common/ModalPdfViewer';
import { buildMessageHistoryTranscript } from '../../../../utils/buildMessageHistoryTranscript';
import { useGetAppointmentRequestMessages } from '../../../../hooks/appointment-requests/useGetAppointmentRequestMessages';

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

// Request Time | Parent Name | Pet Name row at bottom of scroll area
const REQUEST_DETAILS_FIELD_ORDER = [
  'otherComments',
  'condition',
  'type',
  'doctorFullName',
  'dateTime',
  'parentFullName',
  'petName',
];

export function MainInfoTab() {
  const { appointmentRequestData, doctors, setIsMessageModalOpen } =
    useAppointmentRequestModalContext();
  const { appointmentInfo } = appointmentRequestData;
  const isCompletedRequest = !!appointmentInfo?.id;
  const messageHistoryRef = useRef(null);
  const messageModal = useNestedModal();
  const msgHistoryMessageModal = useNestedModal();
  const msgHistoryImagesModal = useNestedModal();
  const msgHistoryPdfModal = useNestedModal();

  const { data: messages = [] } = useGetAppointmentRequestMessages({
    appointmentRequestId: appointmentRequestData?.id,
    enabled: !!appointmentRequestData?.id,
  });

  const allImages = (messages || []).flatMap((m) =>
    Array.isArray(m.images) ? m.images : [],
  );
  const allPdfs = (messages || []).flatMap((m) =>
    Array.isArray(m.pdfs) ? m.pdfs : [],
  );

  const [copiedHistory, setCopiedHistory] = useState(false);

  const handleCopyHistory = () => {
    const historyMessages = messages.map((m) => {
      if (!m._id) {
        const parts = [
          otherComments?.trim(),
          appointmentRequestData.condition?.trim(),
        ].filter(Boolean);
        return {
          ...m,
          message: parts.length ? parts.join('\n') : 'No comments provided',
          authorType: 'client',
        };
      }
      return m;
    });

    const text = buildMessageHistoryTranscript({
      requestType: 'Appointment Request',
      header: {
        parent: appointmentRequestData.parentFullName,
        pet: appointmentRequestData.petName,
        doctor: appointmentRequestData.doctorFullName,
        appointmentType:
          appointmentRequestData.typeTitle || appointmentRequestData.type,
        submitted: appointmentRequestData.dateTime,
      },
      messages: historyMessages,
    });
    navigator.clipboard.writeText(text).then(() => {
      setCopiedHistory(true);
      setTimeout(() => setCopiedHistory(false), 1500);
    });
  };

  const scrollToMessageHistory = () => {
    messageHistoryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const heads = useAppointmentRequestsTableHeaders();
  const { availability, otherComments } = parseAvailabilityInfo(
    appointmentRequestData.availabilityInfo || '',
  );

  const getDoctorData = (doctorId) => {
    return doctors.filter((doctor) => doctor.value === doctorId);
  };

  const requestType = appointmentRequestData?.type;
  const defaultAppointmentType = requestType
    ? [{ label: requestType, value: requestType }]
    : [];

  const hasPreferredDates =
    appointmentRequestData?.preferredDate?.length > 0;
  const hasDaysAvailability = Object.keys(availability).length > 0;

  const renderAppointmentRequestFields = () => {
    const excludedKeys = [
      'date',
      'time',
      'preferredDate',
      'availabilityInfo',
      'attachments',
      'open',
      'lastMsg',
      'message',
      'messageStatus',
    ];

    const additionalFields = [
      { key: 'dateTime', name: 'Request Time' },
      { key: 'otherComments', name: 'Client Comments' },
    ];

    const headsExtraFields = heads
      .filter(({ key }) => !excludedKeys.includes(key))
      .concat(additionalFields)
      .sort(
        (a, b) =>
          REQUEST_DETAILS_FIELD_ORDER.indexOf(a.key) -
          REQUEST_DETAILS_FIELD_ORDER.indexOf(b.key),
      );

    return headsExtraFields.map(
      ({ key, name, capitalize, display, link: linkToProfile }) => {
        let dataToShow = appointmentRequestData[key];
        const link = linkToProfile?.(appointmentRequestData);
        let addEllipsisClass = !link; // for link ellipsis class is added to to anchor tag

        // Hide request Type/Doctor in lower fields (BEFORE and AFTER).
        if (key === 'typeTitle' || key === 'doctorFullName') {
          return null;
        }

        if (key === 'condition' && !dataToShow.trim()) {
          if (isCompletedRequest) {
            dataToShow = 'No comments provided';
          } else {
            addEllipsisClass = false;
            dataToShow = "No comments provided about the pet's condition";
          }
        }

        if (key === 'condition') {
          addEllipsisClass = false;
          return (
            <div
              key={key}
              className={`${key} modal-main-info-field `}
              style={{ gridArea: key, display: display ?? 'block' }}
            >
              <p className="field-label">{name}</p>
              <div
                style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                className={classNames('field-data', {
                  'text-capitalize': capitalize,
                  ellipsis: addEllipsisClass,
                })}
              >
                <ResponsiveEllipsis
                  text={dataToShow}
                  maxLine={5}
                  ellipsis={
                    <span>
                      ...{' '}
                      <button
                        type="button"
                        className="nav-link-style"
                        onClick={() => messageModal.open(dataToShow)}
                      >
                        View
                      </button>
                    </span>
                  }
                  trimRight
                  basedOn="letters"
                />
              </div>
            </div>
          );
        }

        if (key === 'otherComments') {
          addEllipsisClass = false;
          dataToShow = otherComments || 'No comments provided';
          return (
            <div
              key={key}
              className={`${key} modal-main-info-field `}
              style={{ gridArea: key, display: display ?? 'block' }}
            >
              <p className="field-label">{name}</p>
              <div
                style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                className={classNames('field-data', {
                  'text-capitalize': capitalize,
                  ellipsis: addEllipsisClass,
                })}
              >
                <ResponsiveEllipsis
                  text={dataToShow}
                  maxLine={5}
                  ellipsis={
                    <span>
                      ...{' '}
                      <button
                        type="button"
                        className="nav-link-style"
                        onClick={() => messageModal.open(dataToShow)}
                      >
                        View
                      </button>
                    </span>
                  }
                  trimRight
                  basedOn="letters"
                />
              </div>
            </div>
          );
        }

        return (
          <div
            key={key}
            className={`${key} modal-main-info-field `}
            style={{
              gridArea: key === 'typeTitle' ? 'type' : key,
              display: display ?? 'block',
            }}
          >
            <p className="field-label">{name}</p>
            <p
              className={classNames('field-data', {
                'text-capitalize': capitalize,
                ellipsis: addEllipsisClass,
              })}
              title={dataToShow}
            >
              {link ? (
                <Link to={link} className="ellipsis">
                  {dataToShow}
                </Link>
              ) : (
                dataToShow
              )}
            </p>
          </div>
        );
      },
    );
  };

  return (
    <section className="main-info-tab">
      {!isCompletedRequest && (
        <CreateAppointmentBlock
          appointmentRequestData={appointmentRequestData}
          doctors={doctors}
          defaultDoctor={getDoctorData(appointmentRequestData.doctorId)}
          defaultAppointmentType={defaultAppointmentType}
          onScrollToMessageHistory={scrollToMessageHistory}
        />
      )}

      <div
        className={classNames('scrollable-modal-area', {
          'appointment-details-top': isCompletedRequest,
        })}
      >
        {isCompletedRequest && (
          <div className="appointment-info">
            <div className="appointment-details-header-row">
              <h3>Appointment Details</h3>
              <button
                type="button"
                className="nav-link-style"
                onClick={scrollToMessageHistory}
              >
                Message History
              </button>
            </div>
            <AppointmentInfo
              appointmentInfo={{
                ...appointmentInfo,
                doctor: getDoctorData(appointmentInfo.doctor)[0]?.label || '',
              }}
            />
          </div>
        )}

        {(hasPreferredDates || hasDaysAvailability) && (
          <div
            className={classNames('client-availability-section', {
              'both-present': hasPreferredDates && hasDaysAvailability,
            })}
          >
            <h3 className="availability-notes-title">
              Client Availability and Notes
            </h3>

            {hasPreferredDates && (
              <div className="modal-table specific-dates">
                <p className="field-label">Specific Dates & Times</p>
                <PreferredDatesTable
                  preferredDates={appointmentRequestData.preferredDate}
                />
              </div>
            )}
            {hasDaysAvailability && (
              <div className="modal-table availability">
                <p className="field-label">Days of the Week</p>
                <AvailabilityTable availability={availability} />
              </div>
            )}
          </div>
        )}

        <div className="request-additional-info">
          <div className="modal-main-info-data of-request short-variant">
            {renderAppointmentRequestFields()}
          </div>
        </div>

        <div
          ref={messageHistoryRef}
          className="message-history-table modal-table"
        >
          <table className="message-history-toolbar">
            <tbody>
              <tr>
                <td className="toolbar-title-cell">
                  <div className="toolbar-title-row">
                    <h3 className="large">Message History</h3>
                    <Button
                      type="button"
                      className="btn-utility"
                      onClick={handleCopyHistory}
                    >
                      <ContentCopyIcon size={16} aria-hidden />
                      Copy History
                    </Button>
                    {copiedHistory && (
                      <span className="copied-hint">Copied!</span>
                    )}
                  </div>
                </td>
                <td className="action-column">
                  {allImages.length > 0 && (
                    <button
                      type="button"
                      className="nav-link-style history-attachment-link"
                      onClick={() => msgHistoryImagesModal.open(allImages)}
                    >
                      {`Images (${allImages.length})`}
                    </button>
                  )}
                </td>
                <td className="action-column">
                  {allPdfs.length > 0 && (
                    <button
                      type="button"
                      className="nav-link-style history-attachment-link"
                      onClick={() => msgHistoryPdfModal.open(allPdfs)}
                    >
                      {`PDFs (${allPdfs.length})`}
                    </button>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="messages-container">
            <DataTable
              columns={[
                {
                  header: 'Date',
                  field: 'date',
                  type: COLUMN_TYPES.TEXT,
                  headerClassName: 'date-column',
                },
                {
                  header: 'Time',
                  field: 'time',
                  type: COLUMN_TYPES.TEXT,
                  headerClassName: 'time-column',
                },
                {
                  header: (
                    <div className="message-column-header">
                      <span>Messages</span>
                      <span className="client-messages-label">
                        Client messages
                      </span>
                      <Button
                        className="save-update"
                        color="primary"
                        onClick={() => setIsMessageModalOpen(true)}
                      >
                        Create Message
                      </Button>
                    </div>
                  ),
                  field: 'message',
                  type: COLUMN_TYPES.TRUNCATED_TEXT,
                  headerClassName: 'stretched-column',
                  maxLines: 10,
                  ellipsisRenderer: (message, rowData) => (
                    <span>
                      ...{' '}
                      <button
                        type="button"
                        className="nav-link-style"
                        onClick={() => msgHistoryMessageModal.open(rowData)}
                      >
                        View
                      </button>
                    </span>
                  ),
                },
                {
                  header: 'Images',
                  field: 'images',
                  type: COLUMN_TYPES.IMAGES,
                  headerClassName: 'action-column',
                  onImageClick: (rowData) =>
                    msgHistoryImagesModal.open(rowData?.images || []),
                  buttonText: (count) => `View (${count})`,
                },
                {
                  header: 'PDFs',
                  field: 'pdfs',
                  type: COLUMN_TYPES.PDFS,
                  headerClassName: 'action-column',
                  onPdfClick: (rowData) =>
                    msgHistoryPdfModal.open(rowData?.pdfs || []),
                  renderCell: (count) =>
                    count?.length ? `View (${count.length})` : '-',
                },
              ]}
              rows={
                messages.map((m) => {
                  if (!m._id) {
                    const parts = [
                      otherComments?.trim(),
                      appointmentRequestData.condition?.trim(),
                    ].filter(Boolean);
                    return {
                      ...m,
                      message: parts.length
                        ? parts.join('\n')
                        : 'No comments provided',
                      rowCustomClassName: 'client-message',
                    };
                  }
                  return {
                    ...m,
                    rowCustomClassName:
                      m.authorType === 'client' && 'client-message',
                  };
                }) || []
              }
              noDataMessage="No message history available for this appointment request."
              noDataColSpan={5}
            />
          </div>
        </div>
      </div>

      <TextDisplayModal
        modalClassName="centered"
        isOpen={messageModal.isOpen}
        onClose={messageModal.close}
        title="Message"
        content={messageModal?.record || ''}
      />

      <TextDisplayModal
        modalClassName="centered"
        isOpen={msgHistoryMessageModal.isOpen}
        onClose={msgHistoryMessageModal.close}
        title="Message"
        content={
          msgHistoryMessageModal?.record?.message ??
          msgHistoryMessageModal?.record ??
          ''
        }
      />

      <ImageGalleryModal
        modalClassName="centered"
        isOpen={msgHistoryImagesModal.isOpen}
        onClose={msgHistoryImagesModal.close}
        title="Image Viewer"
        images={msgHistoryImagesModal?.record || []}
      />

      <ModalPdfViewer
        isOpen={msgHistoryPdfModal.isOpen}
        dataPdf={msgHistoryPdfModal?.record || []}
        onClose={msgHistoryPdfModal.close}
      />
    </section>
  );
}
